// L6 tramo 4 · Clave de referencia (refKey) ÚNICA para leer una vinculación SII, tanto desde la fila
// del puente (`facturas_sii_vinculaciones`) como desde la oportunidad (fila de la grilla). Reemplaza
// las N reimplementaciones inline de `${venta_tipo}:${venta_ref_id}:${venta_ref_index ?? ""}` (10.1) —
// diagnóstico §A: esa key era OPAQUE-OK para lectura, pero construida por duplicado en cada consumidor,
// con el riesgo de divergir en silencio si uno se actualiza y otro no (caso real: `VentasFacturas/index.js`
// `getVinculacionSII` no distinguía HW-CAPEX de Gestión/HW-OPEX, a diferencia de `useVFDataLoading`).
//
// Cartelería y hardware (`negocios_carteleria` / `negocios_carteleria_hw`): la key pasa a ser
// `linea:<linea_id>` — el id ESTABLE de `lineas_facturacion` (L4), no la posición en el array. Nunca
// colisiona entre gestión/opex/hardware del mismo negocio (son líneas distintas, §A.1). Pauta, BTL y
// contratos: la key de HOY, sin cambios (siguen fuera de la migración a linea_id).

const esCarteleriaTipo = (t) => t === "negocios_carteleria" || t === "negocios_carteleria_hw";

/**
 * Key de una fila del puente `facturas_sii_vinculaciones`.
 * @param {{venta_tipo?:string, venta_ref_id?:(number|string), venta_ref_index?:number, linea_id?:(number|string)}} fila
 * @returns {string|null}
 */
export const refKeyDeVinculacion = (fila) => {
  if (!fila || fila.venta_tipo == null) return null;
  if (esCarteleriaTipo(fila.venta_tipo)) {
    return fila.linea_id != null ? `linea:${fila.linea_id}` : null;
  }
  if (fila.venta_ref_id == null) return null;
  return `${fila.venta_tipo}:${fila.venta_ref_id}:${fila.venta_ref_index ?? ""}`;
};

/**
 * Key de una oportunidad (fila de la grilla de VentasFacturas). Cartelería/hardware se identifican por
 * `op.negocio_id` (ambos tipos comparten esa forma); `op.linea_id` es el id estable resuelto por el
 * loader (L2, ex `linea_id_sombra` — deja de ser sombra en este tramo).
 * @param {{negocio_id?:(number|string), linea_id?:(number|string), type?:string, id?:(number|string),
 *          contrato_id?:(number|string), mes_index?:number}} op
 * @returns {string|null}
 */
export const refKeyDeOportunidad = (op) => {
  if (!op) return null;
  if (op.negocio_id) {
    return op.linea_id != null ? `linea:${op.linea_id}` : null;
  }
  if (op.type === "pauta") return `sales:${op.id}:`;
  if (op.type === "BTL") return `sales_btl:${op.id}:`;
  if (op.type === "contrato") return `sales_contratos:${op.contrato_id}:${op.mes_index ?? ""}`;
  return null;
};

/**
 * Key LEGACY (posicional) de una fila del puente — SOLO para el dual-run de auditoría (L2/L6) y para
 * `plazosCobroSII` mientras `FlujoCajaV2/index.js` no pueda migrar (tramo 4b). Nunca usar para decidir
 * nada nuevo: es el método que esta migración reemplaza.
 * @param {{venta_tipo?:string, venta_ref_id?:(number|string), venta_ref_index?:number}} fila
 * @returns {string|null}
 */
export const refKeyLegacyDeVinculacion = (fila) => {
  if (!fila || fila.venta_tipo == null || fila.venta_ref_id == null) return null;
  return `${fila.venta_tipo}:${fila.venta_ref_id}:${fila.venta_ref_index ?? ""}`;
};

/**
 * Key LEGACY (posicional) de una oportunidad — mismo uso que `refKeyLegacyDeVinculacion`, del lado
 * oportunidad. Cartelería/hardware usan `sii_ref_index` (el espejo posicional que el loader sigue
 * calculando hasta el tramo 4b); pauta/BTL/contratos son la misma key que `refKeyDeOportunidad`.
 * @param {{negocio_id?:(number|string), sii_ref_index?:number, type?:string, id?:(number|string),
 *          contrato_id?:(number|string), mes_index?:number}} op
 * @returns {string|null}
 */
export const refKeyLegacyDeOportunidad = (op) => {
  if (!op) return null;
  if (op.negocio_id) {
    if (op.sii_ref_index == null) return null;
    const tipoCart = op.type === "HW-CAPEX" ? "negocios_carteleria_hw" : "negocios_carteleria";
    return `${tipoCart}:${op.negocio_id}:${op.sii_ref_index}`;
  }
  return refKeyDeOportunidad(op);
};
